import type { EmailAttachment, ParsedEmail } from './emailParse';
import { decodeMsgHtmlBytes, extractHtmlFromCompressedRtf } from './msgRtfHtml';

interface MsgFieldsData {
  dataType?: string | null;
  error?: string;
  subject?: string;
  senderName?: string;
  senderEmail?: string;
  senderSmtpAddress?: string;
  sentRepresentingSmtpAddress?: string;
  body?: string;
  bodyHtml?: string;
  html?: Uint8Array;
  compressedRtf?: Uint8Array;
  internetCodepage?: number;
  messageCodepage?: number;
  clientSubmitTime?: string;
  messageDeliveryTime?: string;
  creationTime?: string;
  attachments?: MsgFieldsData[];
  recipients?: MsgFieldsData[];
  fileName?: string;
  fileNameShort?: string;
  name?: string;
  extension?: string;
  attachMimeTag?: string;
  pidContentId?: string;
  attachmentHidden?: boolean;
  dataId?: number;
  innerMsgContent?: true;
  recipType?: 'to' | 'cc' | 'bcc';
  smtpAddress?: string;
  email?: string;
}

interface MsgReaderInstance {
  parserConfig?: { ansiEncoding?: string; includeRawProps?: boolean };
  getFileData(): MsgFieldsData;
  getAttachment(attach: number | MsgFieldsData): { fileName: string; content: Uint8Array };
}

export async function parseMsg(bytes: Uint8Array): Promise<ParsedEmail> {
  const { MsgReader } = await import('@kenjiuno/msgreader-web-ng');
  const reader = new MsgReader(bytes) as MsgReaderInstance;
  reader.parserConfig = { ansiEncoding: 'utf-8', includeRawProps: false };
  const data = reader.getFileData();

  if (data.error) {
    throw new Error(data.error);
  }

  const headers: Record<string, string> = {};
  const from = formatSender(data);
  if (from) headers.from = from;

  const to = formatRecipients(data.recipients, 'to');
  const cc = formatRecipients(data.recipients, 'cc');
  if (to) headers.to = to;
  if (cc) headers.cc = cc;

  if (data.subject) headers.subject = data.subject;
  const date = data.clientSubmitTime ?? data.messageDeliveryTime ?? data.creationTime;
  if (date) headers.date = date;

  let bodyHtml: string | null = await resolveMsgBodyHtml(data);

  const bodyText = data.body ?? null;
  const attachments: EmailAttachment[] = [];

  for (const att of data.attachments ?? []) {
    if (att.dataType !== 'attachment') continue;

    const name = att.fileName ?? att.fileNameShort ?? att.name ?? 'attachment';
    let attachmentBytes: Uint8Array;
    try {
      attachmentBytes = reader.getAttachment(att).content;
    } catch {
      continue;
    }

    const contentId = att.pidContentId?.replace(/^<|>$/g, '').trim() || undefined;
    attachments.push({
      fileName: att.innerMsgContent ? ensureMsgExtension(name) : name,
      contentType: mimeFromAttachment(att),
      bytes: attachmentBytes,
      contentId,
      isDispositionAttachment: !contentId,
    });
  }

  return {
    headers,
    bodyHtml,
    bodyText,
    attachments,
  };
}

async function resolveMsgBodyHtml(data: MsgFieldsData): Promise<string | null> {
  if (data.bodyHtml?.trim()) return data.bodyHtml;

  if (data.html?.length) {
    const codepage = data.internetCodepage ?? data.messageCodepage;
    const html = decodeMsgHtmlBytes(data.html, codepage);
    if (html.trim()) return html;
  }

  if (data.compressedRtf?.length) {
    const html = await extractHtmlFromCompressedRtf(data.compressedRtf);
    if (html?.trim()) return html;
  }

  return null;
}

function formatSender(data: MsgFieldsData): string | undefined {
  const name = data.senderName;
  const email = data.senderSmtpAddress ?? data.senderEmail ?? data.sentRepresentingSmtpAddress;
  if (name && email) return `${name} <${email}>`;
  return name ?? email ?? undefined;
}

function formatRecipients(
  recipients: MsgFieldsData[] | undefined,
  type: 'to' | 'cc',
): string | undefined {
  if (!recipients?.length) return undefined;
  const lines = recipients
    .filter((recipient) => recipient.recipType === type)
    .map((recipient) => {
      const email = recipient.smtpAddress ?? recipient.email;
      const name = recipient.name;
      if (name && email) return `${name} <${email}>`;
      return email ?? name ?? '';
    })
    .filter(Boolean);
  return lines.length > 0 ? lines.join(', ') : undefined;
}

function mimeFromAttachment(att: MsgFieldsData): string {
  if (att.attachMimeTag) return att.attachMimeTag;
  if (att.innerMsgContent) return 'application/vnd.ms-outlook';

  const ext = (att.extension ?? extFromName(att.fileName ?? att.fileNameShort ?? '')).toLowerCase();
  const map: Record<string, string> = {
    '.png': 'image/png',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.gif': 'image/gif',
    '.webp': 'image/webp',
    '.bmp': 'image/bmp',
    '.svg': 'image/svg+xml',
    '.txt': 'text/plain',
    '.pdf': 'application/pdf',
    '.msg': 'application/vnd.ms-outlook',
    '.html': 'text/html',
    '.htm': 'text/html',
  };
  return map[ext] ?? 'application/octet-stream';
}

function extFromName(name: string): string {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx) : '';
}

function ensureMsgExtension(name: string): string {
  return name.toLowerCase().endsWith('.msg') ? name : `${name}.msg`;
}
